import { useData } from 'vike-react/useData';
import { useState } from 'react';
import { ArrowRight, BookOpen, Search } from 'lucide-react';
import type { Data } from './+data';
import { Container } from '../../components/layout/Container';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { FilterPill } from '../../components/ui/filter-pill';
import { Input } from '../../components/ui/input';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { Reveal } from '../../components/motion/Reveal';
import { getStoredAudience } from '../../lib/audience-storage';
import { AUDIENCES } from '@shared/constants/audiences';

const AUDIENCE_FILTERS = [
  { id: '', label: 'Tous' },
  ...AUDIENCES.map((a) => ({ id: a.id, label: a.shortLabel })),
];

export default function BlogIndexPage() {
  const { posts } = useData<Data>();
  const storedAudience = getStoredAudience();
  const [audienceFilter, setAudienceFilter] = useState<string>(storedAudience ?? '');
  const [query, setQuery] = useState('');

  const filtered = posts.filter((p) => {
    const matchAudience =
      !audienceFilter || (p as { tags?: string[] }).tags?.includes(audienceFilter);
    const matchQuery =
      !query ||
      p.title.toLowerCase().includes(query.toLowerCase()) ||
      (p.excerpt ?? '').toLowerCase().includes(query.toLowerCase());
    return matchAudience && matchQuery;
  });

  const pillar = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="bg-white">
      {/* ── Hero ── */}
      <div className="relative overflow-hidden border-b border-stone-200 bg-stone-50/60 py-14 md:py-20">
        {/* Halos décoratifs */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-32 h-[360px] w-[360px] rounded-full bg-brand-100/50 blur-[110px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 bottom-[-160px] h-[320px] w-[320px] rounded-full bg-accent-100/40 blur-[110px]"
        />
        <Container className="relative">
          <Reveal>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 ring-1 ring-inset ring-brand-200/60">
                <BookOpen className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
              </span>
              <p className="eyebrow">Veille & analyses</p>
            </div>
            <h1 className="h-hero mt-4">
              Le blog <span className="text-gradient">Solution Éco France</span>
            </h1>
            <p className="mt-4 max-w-xl text-[16px] leading-relaxed text-stone-600">
              Marché de l'énergie, jurisprudence CEE, dispositifs d'aide — analyses documentées
              publiées par nos experts.
            </p>
          </Reveal>

          {/* ── Filters ── */}
          <div className="mt-8 flex flex-wrap items-center gap-2">
            {AUDIENCE_FILTERS.map((f) => (
              <FilterPill
                key={f.id}
                active={audienceFilter === f.id}
                onClick={() => setAudienceFilter(f.id)}
              >
                {f.label}
              </FilterPill>
            ))}
            <div className="relative w-full sm:ml-auto sm:w-auto">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" />
              <Input
                className="h-10 w-full pl-9 text-[13px] sm:w-56"
                placeholder="Rechercher…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
        </Container>
      </div>

      <Container className="py-14 md:py-16">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <p className="max-w-md text-[15px] text-stone-600">
              {query
                ? `Aucun article ne correspond à « ${query} ».`
                : 'Aucun article pour ce filtre audience.'}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={() => {
                  setAudienceFilter('');
                  setQuery('');
                }}
              >
                Réinitialiser les filtres
              </Button>
              <Button asChild variant="brand" size="md">
                <a href="/outils">
                  Voir nos outils gratuits
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" strokeWidth={2.2} />
                </a>
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* ── Pillar article ── */}
            {pillar && (
              <Reveal className="mb-12">
                <a
                  href={`/blog/${pillar.slug}`}
                  className="group grid overflow-hidden rounded-2xl border border-stone-200 bg-stone-50/40 transition-all hover:border-brand-200 hover:shadow-medium md:grid-cols-5"
                >
                  <div className="p-8 md:col-span-3 md:p-10">
                    {pillar.category && (
                      <Badge variant="secondary" className="mb-3">
                        {pillar.category.name}
                      </Badge>
                    )}
                    <h2 className="text-balance font-display text-2xl font-bold tracking-tight text-ink group-hover:text-brand-700 md:text-3xl">
                      {pillar.title}
                    </h2>
                    {pillar.excerpt && (
                      <p className="mt-3 text-[15px] leading-relaxed text-stone-600">
                        {pillar.excerpt}
                      </p>
                    )}
                    <div className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-700">
                      Lire l'article{' '}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </div>
                    {pillar.publishedAt && (
                      <time
                        className="mt-4 block text-[11px] text-stone-400"
                        dateTime={pillar.publishedAt.toISOString()}
                      >
                        {new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(
                          new Date(pillar.publishedAt),
                        )}
                      </time>
                    )}
                  </div>
                  <div className="relative hidden items-center justify-center overflow-hidden bg-gradient-to-br from-brand-50 to-accent-50/30 p-8 md:col-span-2 md:flex">
                    <img
                      src="/sef-symbol.svg"
                      alt=""
                      aria-hidden
                      loading="lazy"
                      decoding="async"
                      className="pointer-events-none absolute -bottom-10 -right-8 h-44 w-44 opacity-[0.07] transition-transform duration-500 ease-out group-hover:scale-105"
                    />
                    <BookOpen className="h-24 w-24 text-brand-200" strokeWidth={0.8} />
                  </div>
                </a>
              </Reveal>
            )}

            {/* ── Grid ── */}
            {rest.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((p, i) => (
                  <Reveal key={p.id} delay={i * 0.05}>
                    <a
                      href={`/blog/${p.slug}`}
                      className="group flex h-full flex-col rounded-xl border border-stone-200 p-6 transition-all hover:border-brand-200 hover:shadow-subtle"
                    >
                      {p.category && (
                        <span className="text-[11px] font-bold uppercase tracking-wider text-brand-600">
                          {p.category.name}
                        </span>
                      )}
                      <h2 className="mt-2 font-display text-[17px] font-semibold leading-snug text-ink group-hover:text-brand-700">
                        {p.title}
                      </h2>
                      {p.excerpt && (
                        <p className="mt-2 line-clamp-3 text-[13px] leading-relaxed text-stone-500">
                          {p.excerpt}
                        </p>
                      )}
                      <div className="mt-auto flex items-center justify-between pt-4">
                        {p.publishedAt && (
                          <time
                            className="text-[11px] text-stone-400"
                            dateTime={p.publishedAt.toISOString()}
                          >
                            {new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }).format(
                              new Date(p.publishedAt),
                            )}
                          </time>
                        )}
                        <ArrowRight className="h-3.5 w-3.5 text-stone-300 transition-transform group-hover:translate-x-0.5 group-hover:text-brand-500" />
                      </div>
                    </a>
                  </Reveal>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── CTA ── */}
        <div className="mt-16 rounded-2xl border border-stone-200 bg-stone-50/60 p-8 text-center">
          <h2 className="font-display text-xl font-bold text-ink">Votre situation est unique</h2>
          <p className="mt-2 text-[14px] text-stone-600">
            Démarrez un diagnostic personnalisé pour identifier vos primes CEE en 5 minutes.
          </p>
          <Button asChild variant="primary" size="xl" className="mt-5">
            <a href="/outils">
              Diagnostiquer ma situation <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
        </div>
      </Container>

      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Blog', url: 'https://solutionecofrance.fr/blog' },
        ]}
      />
    </div>
  );
}
